import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { BudgetProgressView, BudgetScope, Category, Currency, DailyBudget, ForecastMode } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { amountValue, money, parseAmount } from "../../shared/lib/format";
import { archiveBudget, createBudget, createBudgetSeries, stopBudgetSeries, updateBudget, type PlanningPeriod } from "./api";
import { Layered } from "./Layers";
import { PagerControls } from "../../shared/ui/Pager";
import { ChipGroup } from "../../shared/ui/ChipGroup";
import { Modal } from "../../shared/ui/Modal";
import { RowMenu } from "../../shared/ui/RowMenu";
import { usePager } from "../../shared/ui/usePager";
import { BudgetTransactionsDialog } from "./BudgetTransactionsDialog";
import { BudgetCalendar } from "./BudgetCalendar";
import { BudgetRecurrenceFields, initialBudgetSchedule, validateBudgetSchedule } from "./BudgetRecurrenceFields";
import { EmptyState } from "../../shared/ui/EmptyState";

const BUDGET_PAGE = 10;

// The two kinds of budget, in the reader's terms. Which one a limit is decides
// whether the spending a plan already owns counts against it.
const SCOPES: { value: BudgetScope; label: string }[] = [
  { value: "specific", label: "For one thing" },
  { value: "general", label: "For everything" },
];

function scopeNote(scope: BudgetScope): string {
  return scope === "general"
    ? "One ceiling over these categories. Everything counts, the spending your plan items and groups own included."
    : "A limit on its own. Spending that a plan item or group already accounts for is that plan's business and does not count here.";
}

interface BudgetsPanelProps {
  bookID: string;
  baseCurrency: Currency;
  period: PlanningPeriod;
  budgets: BudgetProgressView[];
  daily: DailyBudget;
  categories: Category[];
  onNotify: (message: string) => void;
}

// Budgets are spending limits per period over categories: the only limit
// concept in the product. The overview headline reads their totals.
export function BudgetsPanel({ bookID, baseCurrency, period, budgets, daily, categories, onNotify }: BudgetsPanelProps) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["planning"] });
  const [adding, setAdding] = useState(false);
  const [createRefused, setCreateRefused] = useState("");
  const [schedule, setSchedule] = useState(() => initialBudgetSchedule(period.start, period.end));
  const [form, setForm] = useState({ name: "", limit: "", currency: baseCurrency, categoryIds: [] as string[], forecastMode: "remaining" as ForecastMode, scope: "specific" as BudgetScope });
  const create = useMutation({
    mutationFn: async () => {
      const input = { ...form, limit: parseAmount(form.limit) ?? "" };
      if (schedule.repeating) {
        const result = await createBudgetSeries(bookID, { ...input, startDate: schedule.startDate,
          recurrence: { frequency: schedule.frequency, interval: Number(schedule.interval), timeZone: schedule.timeZone,
            count: schedule.ending === "count" ? Number(schedule.count) : undefined },
          untilDate: schedule.ending === "date" ? schedule.untilDate : undefined,
        });
        return { name: result.series.template.name, repeating: true };
      }
      const result = await createBudget(bookID, { ...input, startDate: schedule.startDate, endDate: schedule.endDate, timeZone: schedule.timeZone });
      return { name: result.budget.name, repeating: false };
    },
    onSuccess: async (result) => {
      setForm({ name: "", limit: "", currency: baseCurrency, categoryIds: [], forecastMode: "remaining", scope: "specific" });
      setAdding(false);
      await refresh();
      onNotify(`${result.repeating ? "Repeating budget" : "Budget"} "${result.name}" created.`);
    },
  });
  const dailyByBudget = new Map(daily.budgets.map((view) => [view.budgetId, view]));
  const pager = usePager(budgets, BUDGET_PAGE);
  const categoryName = new Map(categories.map((category) => [category.id, category.name]));

  return (
    <section className="panel planning-panel" aria-labelledby="budgets-title">
      <div className="panel-heading">
        <div><p className="eyebrow">Budget progress</p><h2 id="budgets-title">Budgets</h2></div>
        <div className="heading-actions">
          <span className="daily-total">Daily allowance <strong>{money(daily.totalDailyAllowance.minor, daily.currency)}</strong>{daily.totalDailyAllowanceReporting ? <small>{money(daily.totalDailyAllowanceReporting.minor, daily.totalDailyAllowanceReporting.currency)}</small> : null}</span>
          <button className="primary-button compact" type="button" onClick={() => { if (!adding) setSchedule(initialBudgetSchedule(period.start, period.end)); setAdding((value) => !value); }}><span>{adding ? "Close" : "Add budget"}</span><span aria-hidden="true">{adding ? "×" : "＋"}</span></button>
        </div>
      </div>
      <Modal
        open={adding} onClose={() => setAdding(false)} title="Add a budget"
        subtitle="Choose a spending limit and its period. Repeat it daily, weekly, monthly or yearly."
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setAdding(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form="budget-form" disabled={create.isPending}>
            <span>{create.isPending ? "Saving…" : "Create budget"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id="budget-form" className="stacked-form" aria-label="Add budget"
          onSubmit={(event) => {
            event.preventDefault();
            if (!form.name.trim() || parseAmount(form.limit) === null) {
              setCreateRefused("A budget needs a name and a limit.");
              return;
            }
            const scheduleError = validateBudgetSchedule(schedule);
            if (scheduleError) { setCreateRefused(scheduleError); return; }
            setCreateRefused("");
            create.mutate();
          }}>
          <div className="field-grid">
            <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Everyday spending" required autoFocus /></label>
            <label>Limit<span className="amount-input"><input value={form.limit} onChange={(event) => setForm({ ...form, limit: event.target.value })} inputMode="decimal" placeholder="3000.00" required /><span>{form.currency}</span></span></label>
          </div>
          <BudgetRecurrenceFields value={schedule} onChange={setSchedule} />
          <fieldset className="category-picker">
            <legend>Categories <small>(none selected = every expense)</small></legend>
            {categories.map((category) => (
              <label key={category.id} className="check"><input type="checkbox" checked={form.categoryIds.includes(category.id)} onChange={(event) => setForm({ ...form, categoryIds: event.target.checked ? [...form.categoryIds, category.id] : form.categoryIds.filter((id) => id !== category.id) })} /><span>{category.name}</span></label>
            ))}
          </fieldset>
          <div className="field">
            <span className="field-label">What this limit is for</span>
            <ChipGroup label="What this limit is for" value={form.scope} options={SCOPES}
              onChange={(value) => setForm({ ...form, scope: value as BudgetScope })} />
            <p className="panel-note">{scopeNote(form.scope)}</p>
          </div>
          <label className="check"><input type="checkbox" checked={form.forecastMode === "remaining"} onChange={(event) => setForm({ ...form, forecastMode: event.target.checked ? "remaining" : "none" })} /><span>Count what is left of this limit in the forecast</span></label>
          <p className="panel-note">
            With this on, the unspent part of the limit is treated as money still expected to leave. With it off, the
            budget only watches.
          </p>
          {createRefused ? <p className="form-error" role="alert">{createRefused}</p> : null}
          {create.error ? <p className="form-error" role="alert">{humanError(create.error)}</p> : null}
        </form>
      </Modal>
      {budgets.length === 0 ? <EmptyState>No budget covers this period. A budget is a spending limit over categories; what you actually spent is derived from the journal.</EmptyState> : null}
      <ul className="plan-list">
        {pager.visible.map((view) => <BudgetRow key={view.budget.id} bookID={bookID} baseCurrency={baseCurrency} period={period} view={view} daily={dailyByBudget.get(view.budget.id)} categoryName={categoryName} allCategories={categories} onNotify={onNotify} onChanged={refresh} />)}
      </ul>
      <PagerControls pager={pager} noun="budgets" step={BUDGET_PAGE} />
    </section>
  );
}

interface BudgetRowProps {
  bookID: string;
  baseCurrency: Currency;
  period: PlanningPeriod;
  view: BudgetProgressView;
  daily?: DailyBudget["budgets"][number];
  categoryName: Map<string, string>;
  allCategories: Category[];
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

function BudgetRow({ bookID, baseCurrency, period, view, daily, categoryName, allCategories, onNotify, onChanged }: BudgetRowProps) {
  const [editing, setEditing] = useState(false);
  const [listing, setListing] = useState(false);
  const [scope, setScope] = useState<BudgetScope>(view.budget.scope);
  const [limit, setLimit] = useState(amountValue(view.budget.limit.minor, view.budget.limit.currency));
  const [refused, setRefused] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>(view.budget.categoryIds ?? []);
  const percent = Math.min(100, Math.max(0, view.percentUsed));
  const fill = percent;
  const save = useMutation({
    mutationFn: () => updateBudget(bookID, view.budget.id, {
      limit: { amount: parseAmount(limit) ?? "", currency: view.budget.limit.currency },
      categoryIds, scope,
    }),
    onSuccess: async () => { setEditing(false); await onChanged(); onNotify(`"${view.budget.name}" updated.`); },
  });
  const toggleForecast = useMutation({
    mutationFn: () => updateBudget(bookID, view.budget.id, { forecastMode: view.budget.forecastMode === "remaining" ? "none" : "remaining" }),
    onSuccess: async () => { await onChanged(); },
  });
  const archive = useMutation({
    mutationFn: () => archiveBudget(bookID, view.budget.id),
    onSuccess: async () => { await onChanged(); onNotify(`"${view.budget.name}" archived.`); },
  });
  const stopRepeating = useMutation({
    mutationFn: () => stopBudgetSeries(bookID, view.budget.seriesId!, new Date().toISOString()),
    onSuccess: async () => { await onChanged(); onNotify(`Future repeats stopped. Started periods of "${view.budget.name}" remain.`); },
  });
  const watches = view.budget.categoryIds.length === 0 ? "every expense" : view.budget.categoryIds.map((id) => categoryName.get(id) ?? id).join(", ");
  // "remaining" means the unspent part of this limit is money the forecast
  // still expects to leave; plan items in these categories are then skipped
  // so the same money is not counted twice.
  const countedInForecast = view.budget.forecastMode === "remaining";
  const rows = view.transactions ?? [];
  const notCounted = rows.filter((row) => row.excluded || row.planned).length;
  return (
    <li className={`plan-row ${view.over ? "over" : ""}`}>
      <div className="plan-row-head">
        <div>
          <strong>{view.budget.name}</strong>
          {view.budget.seriesId ? <small>Repeating budget · {view.budget.timeZone}</small> : null}
          <small>{view.budget.scope === "general" ? "For everything" : "For one thing"} · {watches}</small>
        </div>
        <span className="plan-row-status">
          <span className={`status-pill ${view.over ? "status-overdue" : "status-planned"}`}>{view.over ? `Over limit · ${view.percentUsed}%` : `${view.percentUsed}% used`}</span>
          <RowMenu label={`Actions for ${view.budget.name}`}>
            <button className="row-menu-item" type="button" onClick={() => setEditing((value) => !value)}>{editing ? "Close the form" : view.budget.seriesId ? "Change only this period…" : "Change this budget…"}</button>
            {view.budget.seriesId ? <button className="row-menu-item danger" type="button" disabled={stopRepeating.isPending}
              onClick={() => { if (window.confirm("Stop all future repeats? Periods that have already started keep their limits and history.")) stopRepeating.mutate(); }}>Stop future repeats</button> : null}
            <button className="row-menu-item" type="button" onClick={() => setListing(true)}
              title="Read the transactions behind this figure, and stop counting any of them against this limit.">
              What this budget counts…
            </button>
            <button className="row-menu-item" type="button" disabled={toggleForecast.isPending} onClick={() => toggleForecast.mutate()}
              title={countedInForecast
                ? "Stop expecting the rest of this limit to be spent. The budget keeps watching; only the projection changes."
                : "Expect the rest of this limit to be spent. Plan items in these categories are then skipped, so the same money is not counted twice."}>
              {countedInForecast ? "Stop counting it in the forecast" : "Count what is left in the forecast"}
            </button>
            <button className="row-menu-item danger" type="button" disabled={archive.isPending} onClick={() => { if (window.confirm(`Archive budget "${view.budget.name}"? It stays readable, hidden from active lists.`)) archive.mutate(); }}>Archive this budget</button>
          </RowMenu>
        </span>
      </div>
      <div className="plan-row-amounts">
        <div>
          <span>Spent</span>
          <Layered native={view.spent} base={view.spentBase} reporting={view.spentReporting} baseCurrency={baseCurrency} />
          <small>{notCounted > 0 ? `${view.transactionCount} counted, ${notCounted} not` : `${view.transactionCount} transaction${view.transactionCount === 1 ? "" : "s"}`}</small>
        </div>
        <div><span>Limit</span><Layered native={view.budget.limit} base={view.limitBase} reporting={view.limitReporting} baseCurrency={baseCurrency} /></div>
        <div><span>Remaining</span><Layered native={view.remaining} base={view.remainingBase} reporting={view.remainingReporting} baseCurrency={baseCurrency} /></div>
        <div><span>Per day</span><strong>{daily ? money(daily.dailyAllowance.minor, daily.dailyAllowance.currency) : "—"}</strong><small>{daily ? `${daily.daysRemaining} days left` : ""}</small></div>
        <div>
          <span>In the forecast</span>
          <strong>{countedInForecast ? "Counted" : "Not counted"}</strong>
          <small>{countedInForecast
            ? "what is left of this limit is expected to be spent"
            : "a limit to watch; it changes no projection"}</small>
        </div>
      </div>
      <div className="progress-track dark" role="progressbar" aria-label={`${view.budget.name} used`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={fill} aria-valuetext={`${view.percentUsed}% of the limit`}><span className={view.over ? "over" : ""} style={{ width: `${view.over ? 100 : fill}%` }} /></div>
      <BudgetCalendar view={view} period={period} currency={baseCurrency} />
      <Modal
        open={editing} onClose={() => { setScope(view.budget.scope); setEditing(false); }} title={view.budget.seriesId ? "Change only this period" : "Change this budget"} subtitle={view.budget.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => { setScope(view.budget.scope); setEditing(false); }}>Cancel</button>
          <button className="primary-button compact" type="submit" form={`budget-limit-${view.budget.id}`} disabled={save.isPending}>
            <span>{save.isPending ? "Saving…" : "Save"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id={`budget-limit-${view.budget.id}`} className="stacked-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (parseAmount(limit) === null) {
              setRefused("That is not an amount. Digits, and at most three decimals.");
              return;
            }
            setRefused("");
            save.mutate();
          }}>
          {view.budget.seriesId ? <p className="panel-note">These changes apply only to this budget period. Other periods retain their own limits and categories.</p> : null}
          <label>Limit for this period<span className="amount-input"><input value={limit} onChange={(event) => setLimit(event.target.value)} inputMode="decimal" autoFocus required /><span>{view.budget.limit.currency}</span></span></label>
          <div className="field">
            <span className="field-label">What this limit is for</span>
            <ChipGroup label="What this limit is for" value={scope} options={SCOPES} onChange={(value) => setScope(value as BudgetScope)} />
            <p className="panel-note">{scopeNote(scope)}</p>
          </div>
          <fieldset className="category-picker">
            <legend>Categories <small>(none selected = every expense)</small></legend>
            {allCategories.map((category) => (
              <label key={category.id} className="check">
                <input type="checkbox" checked={categoryIds.includes(category.id)}
                  onChange={(event) => setCategoryIds(event.target.checked
                    ? [...categoryIds, category.id]
                    : categoryIds.filter((id) => id !== category.id))} />
                <span>{category.name}</span>
              </label>
            ))}
          </fieldset>
          <p className="panel-note">Spending is derived from the journal and never changes here. Changing what this budget is for, or its categories, re-reads the same journal from a different angle; no transaction is touched.</p>
          {refused ? <p className="form-error" role="alert">{refused}</p> : null}
          {save.error ? <p className="form-error" role="alert">{humanError(save.error)}</p> : null}
        </form>
      </Modal>
      <BudgetTransactionsDialog open={listing} onClose={() => setListing(false)} bookID={bookID} view={view} onNotify={onNotify} onChanged={onChanged} />
      {save.error || archive.error || toggleForecast.error || stopRepeating.error ? <p className="form-error" role="alert">{humanError(save.error ?? archive.error ?? toggleForecast.error ?? stopRepeating.error)}</p> : null}
    </li>
  );
}
